import { useCallback, useState } from 'react';
import {
  Box, Button, Grid,
  useSnackbar, useTranslations, validateEmail, Panel,
  InputAdornment, IconButton, ClosedEyeIcon, EyeIcon, LogoIcon, B1, ReactHookFormTextField,
} from '@uniqkey-frontend/shared-app';
import { Message, useForm } from 'react-hook-form';
import { login } from '../../services/authService';
import { logException } from '../../services/sentryService';

interface ILoginForm {
  email: string;
  password: string;
}

const LoginPage = () => {
  const { t } = useTranslations();
  const { showError } = useSnackbar();

  const [showPassword, setShowPassword] = useState(false);
  const {
    handleSubmit, formState: { errors }, control,
  } = useForm<ILoginForm>({
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const handleOnSubmit = useCallback(async (form: ILoginForm) => {
    try {
      await login({
        email: form.email.toLowerCase().trim(),
        password: form.password,
      });
    } catch (e) {
      showError({ text: t('toasts.usernameOrEmailIsIncorrect') });
      logException(e, {
        message: 'LoginPage/handleOnSubmit exception',
      });
    }
  }, [showError, t]);

  const toggleShowPassword = useCallback(() => setShowPassword(!showPassword), [showPassword]);

  return (
    <Grid
      container
      className="min-height-100-vh"
      alignItems="center"
      justifyContent="center"
      flexDirection="column"
    >
      <Grid item xs={5}>
        <Panel>
          <form name="login" onSubmit={handleSubmit(handleOnSubmit)}>
            <Box p={4}>
              <Grid container alignItems="center" justifyContent="center">
                <LogoIcon height={70} width={250} color={B1} />
              </Grid>
              <Box mt={4} />
              <Grid container alignItems="center" justifyContent="center" rowSpacing={4}>
                <Grid item xs={12}>
                  <ReactHookFormTextField
                    name="email"
                    control={control}
                    fullWidth
                    error={!!errors.email?.message}
                    label={t('loginPage.email')}
                    helperText={errors.email?.message}
                    rules={{
                      required: t('validation.required'),
                      validate: (email): Message | boolean => {
                        const isValid = validateEmail(email);
                        if (!isValid) {
                          return t('validation.invalidFormat');
                        }
                        return true;
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <ReactHookFormTextField
                    name="password"
                    control={control}
                    fullWidth
                    type={showPassword ? 'text' : 'password'}
                    error={!!errors.password?.message}
                    label={t('loginPage.password')}
                    helperText={errors.password?.message}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton edge="end" onClick={toggleShowPassword}>
                            {showPassword ? <ClosedEyeIcon /> : <EyeIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    rules={{ required: t('validation.required') }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Button fullWidth type="submit">{t('loginPage.login')}</Button>
                </Grid>
              </Grid>
            </Box>
          </form>
        </Panel>
      </Grid>
    </Grid>
  );
};

export default LoginPage;
